import {
  useEffect,
  useMemo,
  useRef,
  type ReactElement,
  type ReactNode,
} from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import {
  WidgetEntityResolverProvider,
  type WidgetChangeOptions,
  type WidgetEntityHandle,
  type WidgetEntityResolver,
} from "@playbookai/ui";
import type { WidgetKind } from "@playbookai/types";
import {
  WIDGET_MERGE_MAP,
  applyWidgetOperations,
  diffWidgetParams,
  emptyWidgetContentMeta,
  type WidgetOperation,
} from "@playbookai/widget-schemas";
import { useApiClient } from "../api/context.js";
import { invalidateSolveFreshness } from "../access/auth-scope.js";
import { personPickErrorMessage } from "../access/solve-people.js";
import {
  widgetsApi,
  type ApiClientConfig,
  type WidgetCommandResult,
  type WidgetResponse,
} from "../api/playbook-be-client.js";
import { notifyWriteError } from "../lib/notify-content-lock.js";
import {
  notifyDroppedWidgetOperations,
  notifyEditSessionUnavailable,
  notifySkippedWidgetOperations,
} from "../lib/notify-widget-operations.js";
import { useWidgetActorName } from "./actor-name.js";

/**
 * Concrete `WidgetEntityResolver` implementation backed by TanStack Query.
 *
 * Read: `useQuery(['widget', id])` against `GET /v1/widgets/:id`.
 * Write: `useMutation` against `PATCH /v1/widgets/:id`, debounced 600 ms per
 * widgetId so rapid typing coalesces into one PATCH. `patch()` updates the
 * query cache **synchronously** (optimistic echo) before the debounced network
 * write fires, so widget bodies — which are fully controlled by `data` with no
 * local buffer — reflect each keystroke immediately. (Without this, the cache
 * only changed on mutation success, so between keystrokes the controlled input
 * re-rendered with the stale value and dropped characters — only the last typed
 * char survived, and only after the 600 ms debounce settled.) The mutation's
 * `onSuccess` then reconciles the cache with the server echo.
 *
 * This component lives in `@playbookai/solve-feature` (not `@playbookai/ui`)
 * so the data-blind library doesn't depend on TanStack Query. The shell
 * mounts this provider once around its router so every canvas / dashboard
 * surface sees the same resolver.
 */
export function WidgetEntityResolverWiring({
  children,
}: {
  readonly children: ReactNode;
}): ReactElement {
  const config = useApiClient();
  const qc = useQueryClient();

  // The resolver itself is stable across renders so the provider value
  // doesn't churn. The internal hook closes over `config` + `qc`.
  const resolver = useMemo<WidgetEntityResolver>(
    () => ({
      useWidget: <TParams,>(widgetId: string): WidgetEntityHandle<TParams> =>
        useWidgetEntity<TParams>(widgetId, config, qc),
    }),
    [config, qc],
  );

  return (
    <WidgetEntityResolverProvider value={resolver}>
      {children}
    </WidgetEntityResolverProvider>
  );
}

/**
 * Widgets with an unsaved / in-flight WHOLE-PARAMS local edit. Module-scoped
 * (not React state) so it's shared across every `useWidgetEntity` instance AND
 * readable by the collab `widget_changed` handler in `SolveWorkspace` —
 * mirroring the module-scoped collab `REGISTRY`. qa-004: the nudge refetch is
 * skipped for a widget in this set so a peer's nudge never stomps the local
 * user's live edit (nor hands them a fresh version that would defeat the
 * save-time 409 guard).
 *
 * **PB-4923 narrowed what goes in here, and the narrowing is the point.** A
 * pending BOUNDED edit no longer suppresses the refetch: the local change is
 * held as a set of field/item operations, so fresh server truth can be taken
 * and the pending operations re-layered on top of it. That is what lets a
 * peer's edit to a DIFFERENT field appear while you are still typing in yours
 * (AC1) — under the old all-or-nothing rule it could not, because any local
 * edit froze the whole widget's live updates until the save settled.
 *
 * Only the whole-params fallback still suppresses, because there is nothing to
 * layer: a complete snapshot cannot be rebased onto newer server content
 * without inventing intent for the fields it did not change.
 *
 * The add/remove lifecycle MUST stay airtight — a leaked id silently freezes
 * that widget's live updates until reload: added when a whole-params write is
 * armed, removed when it settles (success or error) and on unmount.
 */
const pendingWidgetEdits = new Set<string>();

/** Read by `SolveCanvas`'s `widget_changed` handler; see `pendingWidgetEdits`. */
export function hasPendingWidgetEdit(widgetId: string): boolean {
  return pendingWidgetEdits.has(widgetId);
}

/** Test-only: reset the module-scoped dirty set between specs. */
export function __resetPendingWidgetEdits(): void {
  pendingWidgetEdits.clear();
}

/**
 * Internal hook that powers `resolver.useWidget(id)`. Called by `<CanvasWidget>`
 * (and any future consumer) on every render for an entity widget. The
 * widgetId-keyed timer in `pendingPatches` ensures rapid edits coalesce.
 */
/**
 * A command id, guarded because `crypto.randomUUID` needs a secure context and
 * jsdom does not always provide it. The fallback only has to be unique within
 * one browser session — the server pairs the id with a request hash, so a
 * collision across sessions is refused rather than misapplied.
 */
function newCommandId(): string {
  const cryptoApi = globalThis.crypto as Crypto | undefined;
  if (cryptoApi && "randomUUID" in cryptoApi) return cryptoApi.randomUUID();
  return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 10)}`;
}

/** The fields waiting on a person pick's save (assign-person d25). */
interface GrantWaiter {
  readonly resolve: () => void;
  readonly reject: (error: Error) => void;
}

/**
 * Settle the fields waiting on a person pick's save. A refusal of the pick
 * itself (403 cannot add people, 409 no longer eligible) is told under the
 * field and nowhere else; any other failure keeps its usual toast, and the
 * field shows the toast's words too.
 */
function settleGrantWaiters(
  waiters: readonly GrantWaiter[],
  error: unknown | null,
  qc: QueryClient,
): void {
  if (waiters.length === 0) return;
  if (error === null) {
    // The picked person may now be a Viewer: the canvas roster and the rail's
    // search must stop offering them as someone without access.
    void qc.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey;
        return (
          Array.isArray(key) &&
          key[0] === "resource-access" &&
          key[4] === "solve" &&
          (key[6] === "members" || key[6] === "directory")
        );
      },
    });
    for (const waiter of waiters) waiter.resolve();
    return;
  }
  const message =
    personPickErrorMessage(error) ??
    (error instanceof Error && error.message
      ? error.message
      : "Edit not saved");
  for (const waiter of waiters) waiter.reject(new Error(message));
}

/**
 * PB-4923 AC5 — how often a live editing session is renewed, against the
 * server's 45 s TTL (`WIDGET_EDIT_SESSION_TTL_MS`).
 *
 * Comfortably inside it so an ordinary hiccup does not drop a live session, and
 * far enough apart that typing costs one request every twenty seconds rather
 * than one per keystroke.
 */
export const WIDGET_EDIT_SESSION_RENEW_MS = 20_000;

/**
 * How long after the last edit the session is released.
 *
 * Not on the write settling: a person mid-sentence pauses for longer than one
 * debounce, and closing there would make the session flicker — telling the AI
 * "nobody is editing this" between two keystrokes, which is exactly when it is
 * least true. Not never, either: the 45 s expiry would then defer AI edits for
 * three quarters of a minute after somebody walked away.
 */
export const WIDGET_EDIT_SESSION_IDLE_MS = 5_000;

/** Backoff between retries of a failed claim, multiplied by the attempt. */
export const WIDGET_EDIT_SESSION_RETRY_MS = 1_000;

/**
 * How many times a claim is retried before the person is TOLD it failed.
 *
 * A lease that never opens cannot be made safe from the client — the
 * protection is a server-side session — so the honest options are to keep
 * trying and, past a point, to stop pretending. Silence was the previous
 * behaviour and is the one thing that is definitely wrong: it leaves somebody
 * typing into a field they believe an approved AI change will skip.
 */
export const WIDGET_EDIT_SESSION_MAX_ATTEMPTS = 3;

function useWidgetEntity<TParams>(
  widgetId: string,
  config: ApiClientConfig,
  qc: QueryClient,
): WidgetEntityHandle<TParams> {
  // AC6. Read HERE rather than at `WidgetEntityResolverWiring`, which is
  // mounted at the app router above every workspace: the roster and presence
  // that know a person's name belong to ONE workspace, and this hook runs in
  // the consumer's render inside it.
  const nameForActor = useWidgetActorName();
  const query = useQuery<WidgetResponse>({
    queryKey: ["widget", widgetId],
    queryFn: () => widgetsApi.get(config, widgetId),
    staleTime: 10_000,
    // Refetch on window focus so a user who's looked at this widget in
    // another tab sees fresh content when they come back.
    refetchOnWindowFocus: true,
  });

  // Per-widget debounce timer, captured in a ref so it survives re-renders.
  const debounceRef = useRef<{ timer: ReturnType<typeof setTimeout> | null }>({
    timer: null,
  });
  // Clear any pending debounced PATCH on unmount, so a widget removed within the
  // 600 ms window can't fire a stray write after it's gone. A pending timer also
  // means the optimistic echo from patch() was never persisted, so invalidate
  // the cached entry — a remount then refetches server truth instead of showing
  // an edit that never saved.
  useEffect(() => {
    const debounce = debounceRef.current;
    return () => {
      if (debounce.timer) {
        clearTimeout(debounce.timer);
        debounce.timer = null;
        void qc.invalidateQueries({ queryKey: ["widget", widgetId] });
      }
      // Airtight dirty-set cleanup: the component is gone, so this widget is no
      // longer being locally edited — clear it unconditionally (a pending
      // mutation's callback may not fire after unmount). Prevents a leaked
      // entry from freezing the widget's live updates.
      pendingWidgetEdits.delete(widgetId);
    };
  }, [qc, widgetId]);
  // Monotonic id of the most-recently-requested edit. A resolved PATCH only
  // reconciles the cache when its own id is still the latest — so a slow, stale
  // mutation that resolves out of order can't clobber a newer optimistic edit
  // (the bug: a just-deleted letter reappearing, then clearing late). An id
  // (not the params payload) is the right key: an A → B → A edit sequence has
  // three distinct requests, but the first and third carry identical params, so
  // comparing stringified params would let the stale first request match the
  // latest intent.
  const requestSeqRef = useRef(0);
  const latestRequestRef = useRef(0);

  /**
   * PB-4923 — monotonic server truth.
   *
   * `params` here is what the SERVER last said, never the rendered value, and
   * `version` only ever moves forward. The monotonic guard is load-bearing:
   * two writes can be in flight at once, and an older response arriving second
   * carries older content. Taking its `params` would step the baseline
   * BACKWARDS, and the next edit would then describe the difference between old
   * server content and the current screen — which includes fields the user
   * never touched.
   */
  const ackedRef = useRef<{
    version: number;
    params: Record<string, unknown>;
  } | null>(null);

  /**
   * The user's OUTSTANDING INTENT, as operations rather than as a snapshot.
   *
   * This is the correction that matters. An earlier cut derived each command by
   * diffing the latest acknowledged server content against the local
   * whole-params snapshot, and that fabricates intent: if a peer changes field
   * C while you are editing field A, the local snapshot still holds the OLD C,
   * so the diff emits `set_field C = oldC` — a reverse edit to a field you
   * never touched, which is precisely the defect this whole change exists to
   * remove.
   *
   * So intent is captured ONCE, at the moment of the edit, against the value
   * that was on screen when the person made it — and it is kept as operations.
   * Server content is then only ever a base to LAYER those operations onto, and
   * is never consulted to reconstruct what the person meant.
   *
   * Keyed by `operationId` so successive keystrokes on one field coalesce to
   * the last value while edits to different targets accumulate independently.
   */
  const pendingRef = useRef<{
    ops: Map<string, WidgetOperation>;
    /**
     * The complete params of the most recent emission, kept ALWAYS.
     *
     * It is what the legacy path sends, and it is the reason no edit can be
     * dropped: whenever a bounded command cannot be formed — an unmappable
     * change, a kind with no merge map, or simply no acknowledged version yet —
     * there is still a truthful whole-object write to fall back to.
     */
    snapshot: Record<string, unknown> | null;
    /**
     * Set when an emission could not be expressed as operations. A whole-params
     * write subsumes everything before it, and while one is pending the widget
     * cannot accept remote content at all — there is nothing to rebase a
     * complete snapshot onto.
     */
    forceWhole: boolean;
    /**
     * Assign-person d25: a person was picked since the last send, so the next
     * send carries `grantViewerIfMissing` and settles these waiters.
     */
    grant: boolean;
    grantWaiters: GrantWaiter[];
  }>({
    ops: new Map(),
    snapshot: null,
    forceWhole: false,
    grant: false,
    grantWaiters: [],
  });

  /** Operation ids currently on the wire, so a response clears exactly those. */
  const inFlightRef = useRef<readonly string[]>([]);

  /**
   * PB-4923 AC5 — the live editing sessions this tab holds, by target key.
   *
   * Liveness only. Holding one grants nothing and protects nothing: the field
   * revision is what actually guards a committed edit. All a session does is
   * let an approved AI change say WHO is editing instead of the unattributed
   * "this field changed", and let it defer to a person mid-sentence rather than
   * landing on top of them. So every call here is best-effort and every failure
   * is swallowed — a lost session degrades a message, never the protection.
   *
   * This resolver is the only place in the client that knows a TARGET KEY at
   * all (it is what the diff produces), which is why the producer lives here
   * rather than in an editor.
   */
  const sessionsRef = useRef<{
    readonly clientId: string;
    readonly held: Map<
      string,
      {
        /**
         * How many CONTROLS currently hold this key. Two can, legitimately and
         * at the same time: `CanvasWidget` renders the card body and the
         * expanded modal from the same data, so one field has two inputs. The
         * first blur must not drop the claim the other one still needs.
         */
        holders: number;
        /** Renewal while the field stays focused, whether or not it is typed in. */
        heartbeat: ReturnType<typeof setInterval> | null;
        /** Post-blur grace, cancelled if focus comes back. */
        idle: ReturnType<typeof setTimeout> | null;
      }
    >;
  }>({ clientId: newCommandId(), held: new Map() });

  const closeEditSession = (targetKey: string): void => {
    const entry = sessionsRef.current.held.get(targetKey);
    if (entry?.heartbeat != null) clearInterval(entry.heartbeat);
    if (entry?.idle != null) clearTimeout(entry.idle);
    sessionsRef.current.held.delete(targetKey);
    void widgetsApi
      .closeEditSession(config, widgetId, {
        targetKey,
        clientId: sessionsRef.current.clientId,
      })
      .catch(() => undefined);
  };

  const sendEditSession = (targetKey: string, attempt = 0): void => {
    void widgetsApi
      .openEditSession(config, widgetId, {
        targetKey,
        clientId: sessionsRef.current.clientId,
      })
      .catch(() => {
        /* Conservative, not silent. The person is unprotected until this
           succeeds, so a failure RETRIES with backoff while the field is still
           held rather than being dropped on the floor — and if it keeps
           failing, the notice below tells them, because a draft they believe is
           protected and is not is the worst of the three outcomes.

           It cannot be made safe on the client: the protection lives in a
           server-side session, so an unreachable server means no protection.
           What the client owes is to keep trying and to be honest. */
        if (!sessionsRef.current.held.has(targetKey)) return;
        if (attempt + 1 >= WIDGET_EDIT_SESSION_MAX_ATTEMPTS) {
          notifyEditSessionUnavailable(widgetId);
          return;
        }
        setTimeout(
          () => {
            if (!sessionsRef.current.held.has(targetKey)) return;
            sendEditSession(targetKey, attempt + 1);
          },
          WIDGET_EDIT_SESSION_RETRY_MS * (attempt + 1),
        );
      });
  };

  /**
   * AC5 — claim one field for as long as a control holds focus on it.
   *
   * Returns the release, so the caller's blur/unmount is what ends the claim.
   * Focus-driven on purpose: the previous producer claimed on the first
   * `onChange`, which left the entire compose-before-typing window unprotected
   * and dropped the claim 5 s after the last keystroke even while the field
   * was still focused. Somebody thinking mid-sentence is exactly who this is
   * for.
   */
  const beginFieldEdit = (targetKey: string): (() => void) => {
    const held = sessionsRef.current.held;
    const existing = held.get(targetKey);
    if (existing !== undefined) {
      existing.holders += 1;
      if (existing.idle !== null) {
        clearTimeout(existing.idle);
        existing.idle = null;
      }
    } else {
      const entry = {
        holders: 1,
        heartbeat: setInterval(
          () => sendEditSession(targetKey),
          WIDGET_EDIT_SESSION_RENEW_MS,
        ),
        idle: null as ReturnType<typeof setTimeout> | null,
      };
      held.set(targetKey, entry);
      sendEditSession(targetKey);
    }

    let released = false;
    return () => {
      // Idempotent: React may run a teardown twice, and a control that blurs
      // and then unmounts would otherwise decrement twice for one claim.
      if (released) return;
      released = true;
      const entry = held.get(targetKey);
      if (entry === undefined) return;
      entry.holders -= 1;
      if (entry.holders > 0) return;
      // A short grace rather than an immediate close: tabbing between two
      // controls of one field, or a re-render that briefly drops focus, should
      // not flap the claim. Cancelled the moment focus returns.
      entry.idle = setTimeout(
        () => closeEditSession(targetKey),
        WIDGET_EDIT_SESSION_IDLE_MS,
      );
    };
  };

  // Leaving the widget ends every claim immediately: expiry would leave them
  // standing for up to the server's TTL, deferring AI edits to fields nobody is
  // in front of any more.
  useEffect(() => {
    const sessions = sessionsRef.current;
    return () => {
      for (const targetKey of [...sessions.held.keys()]) {
        closeEditSession(targetKey);
      }
    };
    // The empty dep array is deliberate: this closes over refs and the stable
    // `config`/`widgetId`, and listing the callback would re-arm the teardown
    // on every render and release claims mid-edit.
  }, []);

  /**
   * Move the baseline forward, and only forward.
   *
   * Two guards, both load-bearing. A non-numeric version cannot anchor a
   * baseline at all — adopting one would leave `acked` non-null with an
   * undefined version and push the next edit onto the command path with
   * `baseVersion: undefined`. And an OLDER version must be ignored, because two
   * writes can be in flight and the second to arrive may be the first to have
   * been sent; taking its content would step the baseline backwards.
   */
  const adoptServerTruth = (
    version: number | undefined,
    params: Record<string, unknown> | undefined,
  ): boolean => {
    if (typeof version !== "number" || params === undefined) return false;
    const acked = ackedRef.current;
    if (acked !== null && version <= acked.version) return false;
    ackedRef.current = { version, params };
    return true;
  };

  // Outstanding operations are per-mount intent. A remount must start from
  // server truth rather than replaying a departed component's edits onto it,
  // which would resurrect changes the user never saw land.
  useEffect(() => {
    const pending = pendingRef.current;
    return () => {
      pending.ops.clear();
      pending.snapshot = null;
      pending.forceWhole = false;
      pending.grant = false;
      // The debounced send was cancelled, so nothing was saved: a waiter that
      // resolved here would tell the field a pick landed when it did not.
      for (const waiter of pending.grantWaiters)
        waiter.reject(new Error("Edit not saved"));
      pending.grantWaiters = [];
    };
  }, []);

  /**
   * Re-derive what should be on screen: monotonic server truth with the user's
   * still-outstanding operations layered back on top.
   *
   * Any operation the executor now refuses — most often because the item was
   * deleted remotely — is dropped from the pending set and reported, rather
   * than retried forever against content it can no longer reach (AC7).
   */
  const relayer = (kind: WidgetKind | undefined): void => {
    const acked = ackedRef.current;
    if (acked === null) return;
    const pending = pendingRef.current;
    if (kind === undefined || pending.ops.size === 0) {
      qc.setQueryData<WidgetResponse>(["widget", widgetId], (prev) =>
        prev ? { ...prev, params: acked.params, version: acked.version } : prev,
      );
      return;
    }
    const outcome = applyWidgetOperations({
      kind,
      params: acked.params,
      meta: emptyWidgetContentMeta(acked.version),
      operations: [...pending.ops.values()],
      source: "human",
      actorId: null,
      now: Date.now(),
    });
    for (const skipped of outcome.skipped) {
      pending.ops.delete(skipped.operationId);
    }
    if (outcome.skipped.length > 0) {
      notifyDroppedWidgetOperations(widgetId, outcome.skipped);
    }
    qc.setQueryData<WidgetResponse>(["widget", widgetId], (prev) =>
      prev
        ? {
            ...prev,
            params: outcome.params as Record<string, unknown>,
            version: acked.version,
          }
        : prev,
    );
  };

  /**
   * Adopt fresh server truth from a READ (the first fetch, a focus refetch, or
   * a peer's `widget_changed` nudge) and re-layer.
   *
   * This is what lets a peer's edit to a different field appear while you are
   * mid-edit: their change arrives as new server content, your outstanding
   * operations go back on top of it, and only the fields you actually touched
   * are still yours. Guarded on a strictly newer version, so writing the
   * layered result back into the cache below cannot re-trigger it.
   */
  const serverVersion = query.data?.version;
  const serverParams = query.data?.params;
  const serverKind = query.data?.kind as WidgetKind | undefined;
  useEffect(() => {
    const hadBaseline = ackedRef.current !== null;
    if (!adoptServerTruth(serverVersion, serverParams)) return;
    // First load has nothing pending, so this is a no-op then and a genuine
    // rebase afterwards.
    if (hadBaseline) relayer(serverKind);
    // `relayer` is deliberately absent from the deps: it closes over refs and
    // the stable `qc`/`widgetId`, and adding it would re-run this on every
    // render and defeat the version guard.
  }, [serverVersion, serverParams, serverKind]);

  const mutation = useMutation({
    mutationFn: ({
      params,
      version,
      grant,
    }: {
      params: Record<string, unknown>;
      requestId: number;
      version?: number;
      grant: boolean;
      waiters: readonly GrantWaiter[];
    }) =>
      widgetsApi.update(config, widgetId, {
        params,
        ...(version !== undefined ? { version } : {}),
        ...(grant ? { grantViewerIfMissing: true } : {}),
      }),
    onSuccess: (updated, variables) => {
      settleGrantWaiters(variables.waiters, null, qc);
      // PB-1570, and BEFORE the latest-request branch on purpose: the server
      // moved the parent SOLVE's `updatedAt` in the same transaction as this
      // write, and it did so for a SUPERSEDED request just as much as for the
      // current one. Leaving it inside the branch below meant a stale request
      // that succeeded followed by a newer one that FAILED left the detail
      // query valid and quoting the older timestamp, with nothing left to
      // invalidate it. Refetch rather than stamping a client-side time — a
      // fabricated timestamp drifts from the server's on the next reload,
      // which is the same defect in a less visible form.
      invalidateSolveFreshness(qc);
      // PB-4923: the whole-params path also advances the monotonic baseline, so
      // a later bounded edit on the same widget starts from server truth rather
      // than from whatever the last command happened to leave behind.
      //
      // The numeric check is load-bearing, not defensive typing. A response
      // without a version cannot anchor a baseline — adopting it would leave
      // `acked` non-null with an undefined version, and the NEXT edit would
      // then take the command path and send `baseVersion: undefined`. Staying
      // null keeps that widget on the legacy path, which is the only correct
      // answer when the server is not telling us where it is.
      adoptServerTruth(updated.version, updated.params);
      if (variables.requestId === latestRequestRef.current) {
        qc.setQueryData<WidgetResponse>(["widget", widgetId], updated);
        pendingWidgetEdits.delete(widgetId);
        return;
      }
      // A superseded (stale) success still carries an authoritative server
      // `version`. Merge it when it's newer — never regress, never touch
      // `params` (a fresher edit's params are in flight) — so the next debounced
      // PATCH sends a current version and doesn't false-409 against the user's
      // OWN prior save (qa-004). Dropping it here would leave the cache on an old
      // version that the in-flight/next request then conflicts against.
      qc.setQueryData<WidgetResponse>(["widget", widgetId], (prev) =>
        prev && updated.version > prev.version
          ? { ...prev, version: updated.version }
          : prev,
      );
    },
    onError: (error, variables) => {
      settleGrantWaiters(variables.waiters, error, qc);
      // The optimistic echo already wrote this edit into the cache. On failure,
      // reconcile back to server truth so an unsaved edit can't keep looking
      // persisted — but only if this is still the latest edit (otherwise the
      // newer edit's PATCH reconciles, and invalidating now would drop it).
      if (variables.requestId === latestRequestRef.current) {
        void qc.invalidateQueries({ queryKey: ["widget", widgetId] });
        pendingWidgetEdits.delete(widgetId);
        // Widget content editing is REST, not Yjs, so a Collaborator can still
        // type into a locked-content widget and lose it to a 403 — surface the
        // friendly lock toast. A qa-004 version conflict (409) gets its own
        // gentle "reloaded" copy; any OTHER failure (500/network/validation/a
        // non-creator 404) gets a critical toast + log so a lost edit is never
        // silent (shared with the move/delete paths). A refused person pick
        // is told under its field instead (d25).
        if (personPickErrorMessage(error) === null) {
          notifyWriteError(error, qc, "Edit not saved");
        }
      }
    },
  });

  /**
   * PB-4923 — the bounded-operation write.
   *
   * Reconciliation has one rule and it is deliberately NOT "trust the latest
   * response": take the newest server VERSION, and re-layer whatever the user
   * still has outstanding on top of it. That makes response order irrelevant —
   * an older response arriving second contributes nothing but the operations it
   * resolved, and cannot drag content backwards.
   *
   * A skipped operation is reported; the call is a 200, so nothing else would
   * tell the person. It is NOT retried — the plan is explicit that a skip must
   * not be silently regenerated and re-applied as if the original save covered
   * a fresh overwrite.
   */
  const commandMutation = useMutation({
    mutationFn: ({
      commandId,
      operations,
      baseVersion,
      grant,
    }: {
      commandId: string;
      operations: readonly WidgetOperation[];
      baseVersion: number;
      requestId: number;
      sentIds: readonly string[];
      kind: WidgetKind;
      grant: boolean;
      waiters: readonly GrantWaiter[];
    }) =>
      widgetsApi.applyCommand(config, widgetId, {
        commandId,
        operations,
        baseVersion,
        ...(grant ? { grantViewerIfMissing: true } : {}),
      }),
    onSuccess: (result: WidgetCommandResult, variables) => {
      invalidateSolveFreshness(qc);
      settleGrantWaiters(variables.waiters, null, qc);

      // These operations are settled either way: applied, or skipped and
      // reported. Clearing them BEFORE re-layering is what stops an applied
      // change being re-applied on top of the server's own copy of it.
      //
      // Only ids that were NOT re-emitted while in flight: a keystroke landing
      // during the round trip replaces the entry under the same id, and
      // clearing it here would discard that newer intent.
      for (const id of variables.sentIds) {
        const current = pendingRef.current.ops.get(id);
        if (current !== undefined && !variables.operations.includes(current)) {
          continue;
        }
        pendingRef.current.ops.delete(id);
      }
      inFlightRef.current = inFlightRef.current.filter(
        (id) => !variables.sentIds.includes(id),
      );

      // Monotonic: an out-of-order older response advances nothing.
      adoptServerTruth(result.version, result.params);
      relayer(variables.kind);
      // AC6 — the notice names the field and, when it can, the person. The
      // resolver comes from the workspace, which already holds the roster and
      // the live presence list, so naming a skip costs no request.
      notifySkippedWidgetOperations(result, { nameForActor });
    },
    onError: (error, variables) => {
      // A failed request is not a skip: the operations never reached the
      // server. They are dropped rather than kept for the next save, because
      // silently re-sending a write the person was already told had failed is
      // worse than making them redo it — and `notifyWriteError` tells them.
      for (const id of variables.sentIds) pendingRef.current.ops.delete(id);
      inFlightRef.current = inFlightRef.current.filter(
        (id) => !variables.sentIds.includes(id),
      );
      void qc.invalidateQueries({ queryKey: ["widget", widgetId] });
      settleGrantWaiters(variables.waiters, error, qc);
      if (personPickErrorMessage(error) === null) {
        notifyWriteError(error, qc, "Edit not saved");
      }
    },
  });

  const patch = (
    next: unknown,
    options?: WidgetChangeOptions,
  ): Promise<void> | undefined => {
    // Tag this edit with a fresh id and mark it the latest intent, so a
    // later-resolving stale PATCH won't overwrite it (see the callbacks above).
    const requestId = ++requestSeqRef.current;
    latestRequestRef.current = requestId;

    const cached = qc.getQueryData<WidgetResponse>(["widget", widgetId]);
    const kind = cached?.kind as WidgetKind | undefined;
    const pending = pendingRef.current;

    /* PB-4923 — capture the intent NOW, against what was on screen.
       `cached.params` is the value the person was looking at when they made
       this edit, so the difference between it and `next` is exactly what they
       meant — nothing about a peer's concurrent change leaks into it. Deferring
       this to send time and diffing against server content instead is what
       produced reverse edits to untouched fields. */
    const baseline = cached?.params;
    // Always kept, so there is never a state in which an edit has nowhere to go.
    pending.snapshot = next as Record<string, unknown>;
    // Assign-person d25: a pick rides the next send, and its field waits on it.
    let pickSaved: Promise<void> | undefined;
    if (options?.grantViewerIfMissing) {
      pending.grant = true;
      pickSaved = new Promise<void>((resolve, reject) => {
        pending.grantWaiters.push({ resolve, reject });
      });
    }

    /* Decided ONCE, here, and `ackedRef` is part of it deliberately.
       Without an acknowledged version there is no baseline to fence a command
       against, so the write is going to be a whole-params PATCH whatever
       happens at send time — and the widget must be marked dirty NOW, because
       a snapshot cannot be rebased onto a peer's nudge. Leaving that discovery
       until the debounce fired accumulated operations that could never be sent
       and left the widget unprotected for the whole window. */
    const mergeable =
      kind !== undefined &&
      WIDGET_MERGE_MAP[kind]?.disposition === "operations" &&
      baseline !== undefined &&
      ackedRef.current !== null &&
      !pending.forceWhole;

    if (mergeable) {
      const diff = diffWidgetParams(
        kind,
        baseline,
        next as Record<string, unknown>,
      );
      if (diff.unrepresentable.length > 0) {
        // A whole-params write subsumes every bounded operation before it, and
        // from here the widget cannot accept remote content until it settles.
        pending.ops.clear();
        pending.forceWhole = true;
        pendingWidgetEdits.add(widgetId);
      } else {
        for (const operation of diff.operations) {
          pending.ops.set(operation.operationId, operation);
        }
        /* AC5 note: no claim is made here any more. Claiming on CHANGE was the
           defect — it left the compose-before-typing window unprotected and
           expired while the field was still focused. The claim now runs off
           FOCUS, through `beginFieldEdit` on the handle, for as long as a
           control holds the field.

           A control that has not been given a `targetKey` yet therefore leases
           nothing, which is the honest state: the register cannot name what it
           is editing. */
      }
    } else {
      pending.forceWhole = true;
      // qa-004: only a whole-params write freezes the widget's live updates.
      // See `pendingWidgetEdits` — a bounded edit no longer does, because it
      // can be re-layered onto fresh server content.
      pendingWidgetEdits.add(widgetId);
    }

    // Optimistic local echo: update the cache synchronously so the controlled
    // widget body reflects this keystroke on the very next render.
    //
    // REPLACE `params` wholesale — never merge into prev.params. `next` is the
    // complete intended params (bodies send `{...data, field}` for edits, or a
    // key-omitted object to *remove* a field — e.g. Tags drops a quadrant when
    // its value is cleared). A merge would re-add the just-removed key from
    // prev, so a cleared Tags field would snap back to its old value.
    qc.setQueryData<WidgetResponse>(["widget", widgetId], (prev) =>
      prev ? { ...prev, params: next as Record<string, unknown> } : prev,
    );

    if (debounceRef.current.timer) clearTimeout(debounceRef.current.timer);
    debounceRef.current.timer = setTimeout(
      () => {
        debounceRef.current.timer = null;
        const acked = ackedRef.current;
        const snapshot = pending.snapshot;
        const grant = pending.grant;
        const waiters = pending.grantWaiters;
        pending.grant = false;
        pending.grantWaiters = [];

        /* The legacy whole-params write, and the three ways to reach it.
         `forceWhole` is the mapped-but-unexpressible case; the other two are
         "we have no merge map for this kind" and "no acknowledged version yet",
         and BOTH must land here rather than sending nothing. An earlier cut
         returned early when there was no acknowledged version, which silently
         dropped the edit — the exact failure this path exists to prevent.

         The version is omitted when unknown, which is the pre-existing legacy
         behaviour on this endpoint, not a new version-less write: it is the
         same call the widget made before PB-4923 in the same situation. */
        const cannotCommand =
          pending.forceWhole || acked === null || kind === undefined;
        if (cannotCommand) {
          if (snapshot === null) {
            pendingWidgetEdits.delete(widgetId);
            settleGrantWaiters(waiters, null, qc);
            return;
          }
          pending.forceWhole = false;
          pending.ops.clear();
          pending.snapshot = null;
          mutation.mutate({
            params: snapshot,
            requestId,
            ...(acked !== null ? { version: acked.version } : {}),
            grant,
            waiters,
          });
          return;
        }

        // Everything still outstanding travels together, including anything a
        // previous in-flight command has not yet resolved — the receipt makes a
        // duplicate harmless, and leaving an operation behind would lose it.
        const outstanding = [...pending.ops.values()];
        if (outstanding.length === 0) {
          pendingWidgetEdits.delete(widgetId);
          settleGrantWaiters(waiters, null, qc);
          return;
        }
        const sentIds = outstanding.map((operation) => operation.operationId);
        inFlightRef.current = [...inFlightRef.current, ...sentIds];
        commandMutation.mutate({
          // Minted once per send and reused by any retry of it, so the server's
          // receipt recognises the retry instead of applying twice.
          commandId: newCommandId(),
          operations: outstanding,
          baseVersion: acked.version,
          requestId,
          sentIds,
          kind,
          grant,
          waiters,
        });
      },
      // A pick is not typing: it goes now, so its field hears back at once.
      pending.grant ? 0 : 600,
    );
    return pickSaved;
  };

  return {
    data: (query.data?.params as TParams | undefined) ?? undefined,
    ...(query.data?.schemaVersion !== undefined
      ? { schemaVersion: query.data.schemaVersion }
      : {}),
    isLoading: query.isLoading,
    error: (query.error ?? null) as Error | null,
    patch,
    beginFieldEdit,
  };
}
